import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Label,
  StatusBadge,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import { brokeragePortalApi, type BrokerageRosterRow } from "../api/brokerage";
import { LoadingTiles } from "../components/BrokerageBits";

// Brokerage / Sub-Broker PR-7 — the Roster tab: invite a sub-broker (two-sided
// consent — they accept from their own portal) and see the firm's affiliates
// across the full lifecycle (Active / Proposed / Declined / Ended). Production
// numbers are zero for a non-active row (the money aggregation stays active-only).
// Each row opens the sub-broker DETAIL view; the interactive commission-visibility
// control lives there (this tab shows a read-only Shown / Hidden / — badge — one
// writer, no divergence). Portal-local (design-system kit only); no React Query —
// a successful invite re-runs the roster loader.

const STATUS_TONE: Record<string, StatusTone> = {
  Active: "ok",
  Proposed: "warn",
  Declined: "danger",
  Ended: "muted",
};

const detailPath = (subBrokerId: string) => `/portal/brokerage/sub-brokers/${subBrokerId}`;

// The read-only commission-visibility indicator (the interactive Show/Hide control
// lives on the sub-broker detail view). Junior-only: an independent sub-broker is
// paid directly and sees their own commissions, so visibility doesn't apply (N/A);
// a non-active junior shows "—" (settable once the affiliation is active).
function VisibilityBadge({ row }: { row: BrokerageRosterRow }) {
  if (row.brokerType !== "Junior") return <span className="text-gray-mid">N/A</span>;
  if (row.status !== "Active") return <span className="text-gray-mid">—</span>;
  return row.financialVisibility === "None" ? (
    <StatusBadge tone="muted">Hidden</StatusBadge>
  ) : (
    <StatusBadge tone="ok">Shown</StatusBadge>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function BrokerageRosterTab() {
  const toast = useToast();
  const navigate = useNavigate();
  const [rows, setRows] = useState<BrokerageRosterRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  // PR-8 — the invited broker's payment posture. Default Independent (paid
  // directly); Junior routes their deal fees through this firm. Set once at
  // onboarding — it can't be changed later.
  const [asJunior, setAsJunior] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadRoster = useCallback(async () => {
    const r = await brokeragePortalApi.roster();
    setRows(r);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await brokeragePortalApi.roster();
        if (!cancelled) setRows(r);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your sub-brokers."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleInvite() {
    const n = name.trim();
    const e = email.trim();
    if (!n || !e) return;
    setSubmitting(true);
    try {
      const result = await brokeragePortalApi.inviteSubBroker({
        name: n,
        email: e,
        company: company.trim() || undefined,
        asJunior,
      });
      setName("");
      setEmail("");
      setCompany("");
      setAsJunior(false);
      await loadRoster();
      toast.success(
        result.alreadyAffiliated
          ? "This broker is already on your roster — we've re-sent the request."
          : "Invitation sent. The broker will accept it from their portal.",
      );
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't send the invitation. Try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-space-4">
      {/* Invite form — a firm builds its roster from here; the broker accepts. */}
      <section
        aria-label="Invite a sub-broker"
        className="rounded-lg border border-border-light p-space-3"
      >
        <h3 className="m-0 text-small font-semibold text-onyx">Invite a sub-broker</h3>
        <p className="mt-space-1 text-caption text-gray-mid">
          We'll send them a request to join your firm. They accept it from their own portal —
          you'll see the status update here.
        </p>
        <div className="mt-space-3 grid gap-space-3 sm:grid-cols-3">
          <div className="space-y-space-1">
            <Label htmlFor="sub-broker-name">Name</Label>
            <Input
              id="sub-broker-name"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="Full name"
              disabled={submitting}
              autoComplete="off"
            />
          </div>
          <div className="space-y-space-1">
            <Label htmlFor="sub-broker-email">Email</Label>
            <Input
              id="sub-broker-email"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder="name@example.com"
              disabled={submitting}
              autoComplete="off"
            />
          </div>
          <div className="space-y-space-1">
            <Label htmlFor="sub-broker-company">Company (optional)</Label>
            <Input
              id="sub-broker-company"
              value={company}
              onChange={(ev) => setCompany(ev.target.value)}
              placeholder="Company name"
              disabled={submitting}
              autoComplete="off"
            />
          </div>
        </div>
        {/* PR-8 — payment posture. Default Independent; Junior routes fees through
            the firm (Lever C). Set once at onboarding — it can't be changed later. */}
        <div className="mt-space-3 space-y-space-1 sm:max-w-sm">
          <Label htmlFor="sub-broker-type">Payment type</Label>
          <select
            id="sub-broker-type"
            value={asJunior ? "junior" : "independent"}
            onChange={(ev) => setAsJunior(ev.target.value === "junior")}
            disabled={submitting}
            className="w-full h-10 px-space-3 py-[10px] font-body text-body sm:text-small text-onyx bg-white-pure border border-border-light rounded-md transition-colors duration-fast ease-standard focus:outline-none focus:border-gold focus:shadow-goldRing disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="independent">Independent — paid directly</option>
            <option value="junior">Junior — paid through your firm</option>
          </select>
          <p className="text-caption text-gray-mid">
            {asJunior
              ? "Phoenix pays your firm for this broker's deals; your firm pays them off-platform. This can't be changed after onboarding."
              : "This broker is paid directly by Phoenix. This can't be changed after onboarding."}
          </p>
        </div>
        <div className="mt-space-3">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void handleInvite()}
            disabled={submitting || !name.trim() || !email.trim()}
            loading={submitting}
            data-testid="brokerage-invite-submit"
          >
            Send invitation
          </Button>
        </div>
      </section>

      {error ? (
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      ) : !rows ? (
        <LoadingTiles />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No sub-brokers yet"
          description="Invite a sub-broker above — once they accept, they'll appear here with their production."
        />
      ) : (
        <div className="space-y-space-3">
          <p className="text-caption text-gray-mid">
            Your firm's affiliated sub-brokers. A pending invitation shows as “Proposed” until the
            broker accepts. Open a sub-broker to see their production and deals, and to manage what
            they can see. “Commission visibility” shows whether a junior sub-broker can currently see
            the gross their deals generate in their own portal.
          </p>
          <div className="overflow-x-auto rounded-lg border border-border-light">
            <table className="w-full text-small">
              <thead>
                <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
                  <th className="px-space-3 py-space-2 font-medium">Sub-broker</th>
                  <th className="px-space-3 py-space-2 font-medium">Type</th>
                  <th className="px-space-3 py-space-2 font-medium">Status</th>
                  <th className="px-space-3 py-space-2 text-right font-medium">Deals</th>
                  <th className="px-space-3 py-space-2 text-right font-medium">Gross</th>
                  <th className="px-space-3 py-space-2 font-medium">Commission visibility</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.subBrokerId}
                    onClick={() => navigate(detailPath(r.subBrokerId))}
                    className="cursor-pointer border-b border-border-light transition-colors last:border-0 hover:bg-white-warm"
                  >
                    <td className="px-space-3 py-space-2 text-onyx">
                      {/* A real link (keyboard / right-click / deep-link); the row
                          onClick is a mouse convenience — stop it double-firing here. */}
                      <Link
                        to={detailPath(r.subBrokerId)}
                        onClick={(ev) => ev.stopPropagation()}
                        className="font-medium text-onyx hover:text-gold hover:underline"
                      >
                        {r.subBrokerName}
                      </Link>
                      {r.isPrimary && (
                        <span className="ml-space-2 rounded-full bg-white-warm px-space-2 py-px text-caption text-gray-mid">
                          Primary
                        </span>
                      )}
                    </td>
                    <td className="px-space-3 py-space-2">
                      {/* Lead Conversion Router (WS-3) — the sub-broker's payee type. */}
                      {r.brokerType === "Junior" ? (
                        <StatusBadge tone="progress">Junior</StatusBadge>
                      ) : (
                        <span className="text-gray-mid">Independent</span>
                      )}
                    </td>
                    <td className="px-space-3 py-space-2">
                      <StatusBadge tone={STATUS_TONE[r.status] ?? "neutral"}>{r.status}</StatusBadge>
                    </td>
                    <td className="px-space-3 py-space-2 text-right text-gray-mid">{r.dealCount}</td>
                    <td className="px-space-3 py-space-2 text-right text-onyx">
                      {formatCurrency(r.ledgerGross)}
                    </td>
                    <td className="px-space-3 py-space-2">
                      {/* Read-only indicator — the setter lives on the detail view. */}
                      <VisibilityBadge row={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
